import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Button, CircularProgress, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  alpha,
} from '@mui/material';
import { EmojiEvents, FileDownloadOutlined } from '@mui/icons-material';
import {
  scoreColor, labelColor, distToPivotColor, pctOrDash, fmtPrice, fmtScore,
  localizeCopy,
  thSx, tdSx, clampTextSx,
  tablePrimaryTextSx, tableSecondaryTextSx, tableNumericTextSx, tableSubNumericTextSx,
} from './helpers';


const TP_SKELETON_ROW_IDS = ['tp-sk-1', 'tp-sk-2', 'tp-sk-3', 'tp-sk-4', 'tp-sk-5'];
const TopPicksLoadingSkeleton = () => (
  <Box sx={{ mb: 2.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
      <Skeleton variant="circular" width={18} height={18} />
      <Skeleton variant="text" width={140} height={18} />
    </Box>
    <Table size="small" sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
      <TableBody>
        {TP_SKELETON_ROW_IDS.map((id) => (
          <TableRow key={id}>
            <TableCell><Skeleton variant="circular" width={22} height={22} /></TableCell>
            <TableCell><Box><Skeleton variant="text" width={40} height={18} /><Skeleton variant="rounded" width={50} height={14} sx={{ mt: 0.3, borderRadius: 999 }} /></Box></TableCell>
            <TableCell><Skeleton variant="text" width={60} /></TableCell>
            <TableCell><Skeleton variant="text" width={48} /></TableCell>
            <TableCell><Skeleton variant="text" width={36} /></TableCell>
            <TableCell><Box sx={{ display: 'flex', gap: 0.4 }}><Skeleton variant="rounded" width={60} height={18} sx={{ borderRadius: 1 }} /><Skeleton variant="rounded" width={50} height={18} sx={{ borderRadius: 1 }} /></Box></TableCell>
            <TableCell><Skeleton variant="rounded" width={70} height={6} sx={{ borderRadius: 1 }} /></TableCell>
            <TableCell><Skeleton variant="text" width={32} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

// ═══════════════════════════════════════════════════════════════════════════
// TopPicksToolbar (extracted for cognitive complexity)
// ═══════════════════════════════════════════════════════════════════════════


const TopPicksToolbar = ({ topPickRows, topPicksLoading, exportingTopPicksImage, handleExportTopPicksImage, t, T }) => {
  const imminentCount = topPickRows.filter((pick) => pick.accum_break_label === 'break_imminent').length;
  const buttonText = exportingTopPicksImage
    ? t('common.exportingImage', 'Đang xuất ảnh...')
    : t('common.exportImage', 'Xuất ảnh');
  const startIcon = exportingTopPicksImage
    ? <CircularProgress size={14} color="inherit" />
    : <FileDownloadOutlined sx={{ fontSize: 16 }} />;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
        <EmojiEvents sx={{ fontSize: 18, color: '#f59e0b' }} />
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#1e293b' }}>{T('topPicks.title')}</Typography>
        <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8' }}>— {topPickRows.length} {T('topPicks.priorityHint')}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#ef4444' }}>
          {T('topPicks.imminentCount', { count: imminentCount })}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={handleExportTopPicksImage}
          disabled={exportingTopPicksImage || topPicksLoading || topPickRows.length === 0}
          startIcon={startIcon}
          data-html2canvas-ignore="true"
          sx={{
            textTransform: 'none',
            fontSize: '0.72rem',
            fontWeight: 700,
            color: '#475569',
            borderColor: '#cbd5e1',
            bgcolor: '#fff',
            px: 1.2,
            minWidth: 0,
            borderRadius: 2,
            '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' },
            '&:disabled': { borderColor: '#e2e8f0', color: '#cbd5e1' },
            '& .MuiButton-startIcon': { mr: 0.45 },
          }}
        >
          {buttonText}
        </Button>
      </Box>
    </Box>
  );
};
TopPicksToolbar.propTypes = {
  topPickRows: PropTypes.array.isRequired,
  topPicksLoading: PropTypes.bool,
  exportingTopPicksImage: PropTypes.bool,
  handleExportTopPicksImage: PropTypes.func,
  t: PropTypes.func.isRequired,
  T: PropTypes.func.isRequired,
};

// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════

const TopPickRow = ({ pick, idx, lang, t, T, labelText, topPickActionLabel, openDetail }) => {
                  const hc = scoreColor(pick.health_score);
                  const reasons = (pick.pick_reasons || []).slice(0, 2);
                  const localizedReasons = reasons.map((r) => T(`topPicks.reasons.${r}`, { defaultValue: r.replaceAll('_', ' ') }));
                  const distPct = pick.dist_to_pivot;
                  const phaseLbl = pick.wyckoff_phase ? t(`phase.phases.${pick.wyckoff_phase}`, pick.wyckoff_phase) : '—';
                  const triggerGap = pick.close && pick.entry_break_level
                    ? ((pick.entry_break_level - pick.close) / pick.close) * 100
                    : null;
                  return (
                    <TableRow key={pick.ticker} onClick={() => openDetail(pick.ticker)} hover sx={{ cursor: 'pointer' }}>
                      <TableCell sx={{ ...tdSx, width: 42 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.45 }}>
                          <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: alpha(hc, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: hc }}>{idx + 1}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ ...tablePrimaryTextSx, fontSize: '0.88rem', fontWeight: 800, lineHeight: 1.15 }}>{pick.ticker}</Typography>
                        <Typography sx={{ ...clampTextSx(3), ...tableSecondaryTextSx, mt: 0.18 }}>
                          {pick.company_name || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ ...clampTextSx(2), ...tablePrimaryTextSx, fontSize: '0.68rem', color: '#475569', fontWeight: 600 }}>
                          {localizeCopy(pick.sector_name, lang) || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ ...tablePrimaryTextSx, fontSize: '0.68rem', color: labelColor(pick.accum_break_label) }}>
                          {labelText(pick.accum_break_label)}
                        </Typography>
                        <Typography sx={{ ...clampTextSx(1), ...tableSecondaryTextSx, mt: 0.18 }}>
                          {pick.wyckoff_phase ? phaseLbl : T('topPicks.noPhase')}
                          {pick.pocket_pivot_5d ? ` • ${T('topPicks.pocketPivot')}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                        <Typography sx={{ ...tableNumericTextSx, color: '#1e293b' }}>{fmtPrice(pick.close)}</Typography>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                        <Typography sx={{ ...tableNumericTextSx, color: '#10b981' }}>{fmtPrice(pick.entry_break_level)}</Typography>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                        <Typography sx={{ ...tableNumericTextSx, color: '#ef4444' }}>{fmtPrice(pick.stop_loss_level)}</Typography>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                        <Typography sx={{ ...tableNumericTextSx, color: distToPivotColor(distPct) }}>
                          {pctOrDash(distPct)}
                        </Typography>
                        {triggerGap == null ? null : (
                          <Typography sx={{ ...tableSubNumericTextSx, mt: 0.15 }}>
                            {T('topPicks.gap', { value: Math.max(triggerGap, 0).toFixed(1) })}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...tdSx, pr: 0.5 }}>
                        {localizedReasons.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                            {localizedReasons.map((reason, reasonIdx) => (
                              <Box key={reason} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.45, minWidth: 0 }}>
                                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: reasonIdx === 0 ? '#64748b' : '#cbd5e1', mt: '0.38rem', flexShrink: 0 }} />
                                <Typography
                                  sx={{
                                    ...clampTextSx(1),
                                    fontSize: reasonIdx === 0 ? '0.65rem' : '0.62rem',
                                    color: reasonIdx === 0 ? '#475569' : '#64748b',
                                    fontWeight: reasonIdx === 0 ? 600 : 500,
                                    lineHeight: 1.45,
                                  }}
                                >
                                  {reason}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: '0.64rem', color: '#94a3b8' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'left', pl: 0.5 }}>
                        <Typography sx={{ ...tableNumericTextSx, fontSize: '0.96rem', fontWeight: 800, color: hc, lineHeight: 1.05 }}>
                          {fmtScore(pick.health_score)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.7, mt: 0.28, flexWrap: 'wrap' }}>
                          <Typography sx={{ ...tableSubNumericTextSx, whiteSpace: 'nowrap' }}>
                            {T('topPicks.row.accum')} <Box component="span" sx={{ color: '#475569', fontWeight: 700, fontFamily: 'monospace' }}>{fmtScore(pick.AccumulationScore)}</Box>
                          </Typography>
                          <Typography sx={{ ...tableSubNumericTextSx, whiteSpace: 'nowrap' }}>
                            {T('topPicks.row.break')} <Box component="span" sx={{ color: '#475569', fontWeight: 700, fontFamily: 'monospace' }}>{fmtScore(pick.BreakReadinessScore)}</Box>
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ ...clampTextSx(2), ...tablePrimaryTextSx, fontSize: '0.66rem', color: hc, maxWidth: 120 }}>
                          {topPickActionLabel(pick)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
};
TopPickRow.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  pick: PropTypes.object,
  idx: PropTypes.number,
  lang: PropTypes.string,
  t: PropTypes.func,
  T: PropTypes.func,
  labelText: PropTypes.func,
  topPickActionLabel: PropTypes.func,
  openDetail: PropTypes.func,
};

// ═══════════════════════════════════════════════════════════════════════════
// StatTilesBlock (extracted for cognitive complexity)
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════

const TopPicksBlock = ({ topPicksLoading, topPicks, topPickRows, topPicksExportRef, exportingTopPicksImage, handleExportTopPicksImage, lang, t, T, labelText, topPickActionLabel, openDetail }) => (
      topPicksLoading && !topPicks ? (
        <TopPicksLoadingSkeleton />
      ) : topPickRows.length > 0 && (
        <Box sx={{ mb: 2.5 }} ref={topPicksExportRef}>
          <TopPicksToolbar
            topPickRows={topPickRows}
            topPicksLoading={topPicksLoading}
            exportingTopPicksImage={exportingTopPicksImage}
            handleExportTopPicksImage={handleExportTopPicksImage}
            t={t}
            T={T}
          />
          <TableContainer sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflowX: { xs: 'auto', lg: 'hidden' }, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <Table size="small" sx={{ width: '100%', minWidth: { xs: 1040, lg: 0 }, tableLayout: { xs: 'fixed', lg: 'auto' }, '& td, & th': { borderColor: '#f1f5f9', py: 0.7, px: 1, verticalAlign: 'middle' } }}>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ ...thSx, width: { xs: 42, lg: '4%' } }}>#</TableCell>
                  <TableCell sx={{ ...thSx, width: { xs: 150, lg: '15%' } }}>{T('topPicks.headers.ticker')}</TableCell>
                  <TableCell sx={{ ...thSx, width: { xs: 118, lg: '9%' } }}>{T('topPicks.headers.sector')}</TableCell>
                  <TableCell sx={{ ...thSx, width: { xs: 150, lg: '11%' } }}>{T('topPicks.headers.setup')}</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right', width: { xs: 92, lg: '7%' } }}>{T('topPicks.headers.price')}</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right', width: { xs: 96, lg: '8%' } }}>{T('topPicks.headers.trigger')}</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right', width: { xs: 92, lg: '8%' } }}>{T('topPicks.headers.stop')}</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right', width: { xs: 86, lg: '7%' } }}>{T('topPicks.headers.pivot')}</TableCell>
                  <TableCell sx={{ ...thSx, width: { xs: 175, lg: '10%' } }}>{T('topPicks.headers.catalyst')}</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'left', width: { xs: 130, lg: '9%' } }}>{T('topPicks.headers.score')}</TableCell>
                  <TableCell sx={{ ...thSx, width: { xs: 150, lg: '12%' } }}>{T('topPicks.headers.action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topPickRows.map((pick, idx) => (
                  <TopPickRow key={pick.ticker} pick={pick} idx={idx} lang={lang} t={t} T={T} labelText={labelText} topPickActionLabel={topPickActionLabel} openDetail={openDetail} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )
);
TopPicksBlock.propTypes = {
  topPicksLoading: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  topPicks: PropTypes.object,
  topPickRows: PropTypes.array,
  topPicksExportRef: PropTypes.shape({ current: PropTypes.any }),
  exportingTopPicksImage: PropTypes.bool,
  handleExportTopPicksImage: PropTypes.func,
  lang: PropTypes.string,
  t: PropTypes.func,
  T: PropTypes.func,
  labelText: PropTypes.func,
  topPickActionLabel: PropTypes.func,
  openDetail: PropTypes.func,
};

// ═══════════════════════════════════════════════════════════════════════════
// SummaryCardsBlock (extracted for cognitive complexity)
// ═══════════════════════════════════════════════════════════════════════════

export default TopPicksBlock;
